/**
 * YieldVarianceSuggestionsBanner — Reusable amber banner showing raw materials
 * with recurring yield variances (2+ occurrences) and suggested yield factor updates.
 * Used on the Yield Variance Report page and the Supervisor Dashboard.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { Info, ChevronDown } from 'lucide-react';
import { formatYield, formatYieldVariance, isYieldFallback } from '../hooks/useYieldFactor';
import yieldVarianceService, { YieldVarianceSuggestion } from '../services/yieldVarianceService';
import { formatMMDDYYYY } from '../utils/dateUtils';

interface YieldVarianceSuggestionsBannerProps {
  /** Compact mode hides the expanded table details for dashboard use */
  compact?: boolean;
}

const YieldVarianceSuggestionsBanner: React.FC<YieldVarianceSuggestionsBannerProps> = ({ compact = false }) => {
  const { t } = useLanguage();

  const [suggestions, setSuggestions] = useState<YieldVarianceSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const response = await yieldVarianceService.getYieldVarianceSuggestions();
        setSuggestions(response.suggestions);
      } catch (err) {
        console.error('Error fetching yield variance suggestions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSuggestions();
  }, []);

  const toggleItemExpansion = useCallback((itemNumber: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemNumber)) {
        next.delete(itemNumber);
      } else {
        next.add(itemNumber);
      }
      return next;
    });
  }, []);


  if (loading || suggestions.length === 0) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl overflow-hidden">
      {/* Banner header */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-800/50 rounded-lg">
            <Info className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              {t('yieldVariance.suggestionsTitle') || 'Recurring Yield Variances'}
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              <span className="font-bold">{suggestions.length}</span>{' '}
              {t('yieldVariance.suggestionsDescription') || 'material(s) have recurring yield variances — review suggested yield factor updates'}
            </p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-amber-600 dark:text-amber-400 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
      </button>

      {/* Expanded suggestions table */}
      {showDetails && (
        <div className="border-t border-amber-200 dark:border-amber-700">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-amber-100/50 dark:bg-amber-900/30">
                  <th className="text-left px-4 py-2 text-amber-800 dark:text-amber-200 font-medium">{t('yieldVariance.finishedProduct') || 'Finished Product'}</th>
                  <th className="text-left px-4 py-2 text-amber-800 dark:text-amber-200 font-medium">{t('yieldVariance.rawMaterial') || 'Raw Material'}</th>
                  <th className="text-center px-4 py-2 text-amber-800 dark:text-amber-200 font-medium">{t('yieldVariance.currentYield') || 'Current Yield'}</th>
                  <th className="text-center px-4 py-2 text-amber-800 dark:text-amber-200 font-medium">{t('yieldVariance.suggestedYield') || 'Suggested Yield'}</th>
                  <th className="text-center px-4 py-2 text-amber-800 dark:text-amber-200 font-medium">{t('yieldVariance.occurrences') || 'Occurrences'}</th>
                  <th className="text-center px-4 py-2 text-amber-800 dark:text-amber-200 font-medium">{t('yieldVariance.lastOccurrence') || 'Last Occurrence'}</th>
                  {!compact && <th className="text-center px-4 py-2 text-amber-800 dark:text-amber-200 font-medium"></th>}
                </tr>
              </thead>
              <tbody>
                {suggestions.map(s => {
                  const rowKey = `${s.product_sku}|${s.item_number}`;
                  // current_expected_yield is null when the (product, material) pair has no
                  // configured yield — show "—" and skip the diff rather than a misleading number.
                  const diff = s.current_expected_yield == null ? null : s.suggested_yield - s.current_expected_yield;
                  const isExpanded = expandedItems.has(rowKey);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr className="border-t border-amber-200/50 dark:border-amber-700/50 hover:bg-amber-50 dark:hover:bg-amber-900/10">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {s.product_name}
                            {s.product_chinese_name && <span className="text-gray-500 dark:text-gray-400"> ({s.product_chinese_name})</span>}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{s.product_sku}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {s.item_name}
                            {s.item_chinese_name && <span className="text-gray-500 dark:text-gray-400"> ({s.item_chinese_name})</span>}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{s.item_number}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {s.current_expected_yield == null ? (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          ) : (
                            <span className={isYieldFallback(s.yield_source) ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}>
                              {formatYield(s.current_expected_yield, 2, s.yield_source)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-semibold ${diff == null ? 'text-gray-700 dark:text-gray-200' : diff >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatYield(s.suggested_yield)}
                          </span>
                          {diff != null && (
                            <span className={`ml-1 text-xs ${diff >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              ({formatYieldVariance(diff)})
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-800/50 dark:text-amber-200">
                            {s.variance_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                          {formatMMDDYYYY(s.last_occurrence)}
                        </td>
                        {!compact && (
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => toggleItemExpansion(rowKey)}
                              className="text-xs text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 underline"
                            >
                              {isExpanded
                                ? (t('yieldVariance.hideSuggestions') || 'Hide')
                                : (t('yieldVariance.showSuggestions') || 'Review')}
                            </button>
                          </td>
                        )}
                      </tr>
                      {/* Expanded: recent variance history with operator/packer */}
                      {!compact && isExpanded && s.recent_variances.length > 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-3 bg-amber-50/50 dark:bg-amber-900/10">
                            <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-2">
                              {t('yieldVariance.recentHistory') || 'Recent Variance History'}
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500 dark:text-gray-400">
                                    <th className="text-left px-2 py-1">{t('yieldVariance.session') || 'Session'}</th>
                                    <th className="text-left px-2 py-1">{t('yieldVariance.date') || 'Date'}</th>
                                    <th className="text-center px-2 py-1">{t('yieldVariance.expectedYield') || 'Expected'}</th>
                                    <th className="text-center px-2 py-1">{t('yieldVariance.actualYield') || 'Actual'}</th>
                                    <th className="text-center px-2 py-1">{t('yieldVariance.variance') || 'Variance'}</th>
                                    <th className="text-left px-2 py-1">{t('yieldVariance.operator') || 'Operator'}</th>
                                    <th className="text-left px-2 py-1">{t('common.packer') || 'Packer'}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.recent_variances.map((v, idx) => (
                                    <tr key={idx} className="border-t border-amber-200/30 dark:border-amber-700/30">
                                      <td className="px-2 py-1 text-gray-700 dark:text-gray-300">{v.sub_session_number || v.session_number}</td>
                                      <td className="px-2 py-1 text-gray-600 dark:text-gray-400">{formatMMDDYYYY(v.completed_at)}</td>
                                      <td className="px-2 py-1 text-center text-gray-700 dark:text-gray-300">{v.expected_yield == null ? '—' : formatYield(v.expected_yield)}</td>
                                      <td className="px-2 py-1 text-center text-gray-700 dark:text-gray-300">{formatYield(v.actual_yield)}</td>
                                      <td className="px-2 py-1 text-center">
                                        {v.variance == null ? (
                                          <span className="text-gray-400 dark:text-gray-500">—</span>
                                        ) : (
                                          <span className={`font-medium ${v.variance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {formatYieldVariance(v.variance)}
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-2 py-1 text-gray-700 dark:text-gray-300">{v.operator_name || '-'}</td>
                                      <td className="px-2 py-1 text-gray-700 dark:text-gray-300">{v.packer_name || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default YieldVarianceSuggestionsBanner;
